/**
 * Pure status helpers shared by TruckMarker (map popup), TruckCard (lists)
 * and the Info live-status grid. Extracted verbatim from TruckMarker so the
 * output text is identical - only the location of the code changed.
 */

/* Statuses of trucks that are actually out (have live GPS). SCHEDULED trucks
   carry a planned zone-centre position, not real GPS, so they get no marker. */
const LIVE_GPS_STATUSES = [
    'STARTUP', 'EN_ROUTE_TO_ZONE', 'IN_ZONE', 'EN_ROUTE_TO_DEPOT', 'CLEANUP',
    'FINISHED', 'ON_BREAK', 'EN_ROUTE_TO_ICECREAM_VAN_RESERVATION',
    'AT_ICECREAM_VAN_RESERVATION',
];

/** A truck with real GPS data - gets a map marker. */
export function hasLiveGps(kar) {
    return LIVE_GPS_STATUSES.includes(kar?.status);
}

/** Whether to show a truck in lists at all. Not-scheduled trucks are hidden. */
export function isVisibleTruck(kar) {
    return kar?.status !== 'NOT_SCHEDULED';
}

/** Local "HH:MM" for an ISO datetime, optionally shifted by addMinutes. */
export function formatLocalTime(iso, addMinutes = 0) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    if (addMinutes) d.setMinutes(d.getMinutes() + addMinutes);
    return d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Human-readable status line for a kar.
 * @param {object} kar - truck object (uses kar.status, kar.status_payload)
 * @param {function} t - translation function t(path, fallback)
 */
export function statusToText(kar, t) {
    const zoneName = kar.status_payload?.scheduled_at_name;
    const scheduledAt = kar.status_payload?.scheduled_at_datetime;

    switch (kar.status) {
        case "STARTUP":
            return zoneName
                ? t('tracker.truckMarker.status.startupWithZone', 'Vertrekt zodadelijk richting {zone}').replace('{zone}', zoneName)
                : t('tracker.truckMarker.status.startup', 'Vertrekt zodadelijk');
        case "EN_ROUTE_TO_ZONE":
            return zoneName
                ? t('tracker.truckMarker.status.enRouteToZoneWithName', 'Onderweg naar {zone}').replace('{zone}', zoneName)
                : t('tracker.truckMarker.status.enRouteToZone', 'Onderweg naar zone');
        case "IN_ZONE":
            return zoneName
                ? t('tracker.truckMarker.status.inZoneWithName', 'Aan het rijden in {zone}').replace('{zone}', zoneName)
                : t('tracker.truckMarker.status.inZone', 'Aan het rijden');
        case "EN_ROUTE_TO_DEPOT":
            return t('tracker.truckMarker.status.soldOut', 'Uitverkocht');
        case "CLEANUP":
            return t('tracker.truckMarker.status.soldOut', 'Uitverkocht');
        case "FINISHED":
            return t('tracker.truckMarker.status.soldOut', 'Uitverkocht');
        case "SCHEDULED": {
            // Departs roughly 15 min after the scheduled start time.
            const departure = formatLocalTime(scheduledAt, 15);
            if (departure && zoneName) {
                return t('tracker.truckMarker.status.scheduledDeparture', 'Vertrekt omstreeks {time} richting {zone}')
                    .replace('{time}', departure).replace('{zone}', zoneName);
            }
            if (departure) {
                return t('tracker.truckMarker.status.scheduledDepartureNoZone', 'Vertrekt omstreeks {time}')
                    .replace('{time}', departure);
            }
            return zoneName
                ? t('tracker.truckMarker.status.scheduledWithZone', 'Gepland in {zone}').replace('{zone}', zoneName)
                : t('tracker.truckMarker.status.scheduled', 'Gepland');
        }
        case "NOT_SCHEDULED":
            return t('tracker.truckMarker.status.notScheduled', 'Niet gepland vandaag');
        case "EN_ROUTE_TO_ICECREAM_VAN_RESERVATION":
            return t('tracker.truckMarker.status.enRouteToReservation', 'Onderweg naar reservatie');
        case "AT_ICECREAM_VAN_RESERVATION": {
            // scheduled_at_datetime here is the planned end of the reservation.
            const until = formatLocalTime(scheduledAt);
            if (until) {
                return t('tracker.truckMarker.status.atReservationUntil', 'Op reservatie tot omstreeks {time}')
                    .replace('{time}', until);
            }
            return t('tracker.truckMarker.status.atReservation', 'Op reservatie');
        }
        case "ON_BREAK":
            return t('tracker.truckMarker.status.onBreak', 'In pauze');
        default:
            return '';
    }
}

/**
 * Truck display number: "#8 Banaan" -> "8", else the id.
 */
export function truckNumber(kar) {
    const match = kar?.title && kar.title.match(/^#(\d+)/);
    return match ? match[1] : kar?.id;
}

/**
 * StatusPill label + variant for a kar.
 */
export function statusPillForKar(kar, t) {
    if (kar?.status === 'SCHEDULED') {
        return { variant: 'scheduled', label: t('tracker.truckMarker.status.plannedShort', 'Gepland') };
    }
    const reservation = kar?.status === 'EN_ROUTE_TO_ICECREAM_VAN_RESERVATION' || kar?.status === 'AT_ICECREAM_VAN_RESERVATION';
    if (reservation) {
        return { variant: 'tan', label: t('tracker.truckMarker.status.reserved', 'Gereserveerd') };
    }
    return kar?.available
        ? { variant: 'green', label: t('tracker.truckMarker.status.available', 'Beschikbaar') }
        : { variant: 'red', label: t('tracker.truckMarker.status.unavailable', 'Niet beschikbaar') };
}
